// Phase 62.E.1 — activation events instrumentation helper.
//
// Wave 5 Lane 2 foundation. Other lanes (3-5) consume these events via
// the activation aggregation hook (useActivationStatus, Lane 3), the
// GettingStartedStrip + HardGateGuard (Lane 3), the re-engagement cron
// (Lane 4), and the admin ActivationFunnel page (Lane 5).
//
// The append-only `activation_events` table is created by migration
// 20260852_phase_62_e_1_activation_events.sql. The 8 funnel stages are
// signed_up → email_verified → profile_complete → payout_connected →
// first_customer_added → first_quote_sent → first_job_scheduled →
// first_paid_invoice.
//
// emitActivationEvent is fire-and-forget: every call is wrapped in
// try/catch so a failing insert never blocks the UX. For `first_*`
// events, we EXISTS-check before insert so a callsite that fires every
// save (CustomerFormModal handleSubmit, etc.) produces at most one row
// per user-event tuple. Non-first events (signed_up, email_verified,
// profile_complete, payout_connected) rely on caller-side transition
// detection.
//
// emitActivationEventServer is the deno parallel for whop-webhook. It
// accepts the service-role supabase client argument rather than reading
// the singleton browser instance.
//
// Type-safety note: the auto-generated src/types/supabase.ts does not
// yet include the activation_events table — codegen runs after the
// migration applies. To unblock the browser build before that codegen
// lands, the helper's `from('activation_events')` call casts via `as
// any` (allowed only here, with an eslint-disable comment). After the
// next `npm run db:types`, this cast can be removed.

import { supabase } from './supabase'

export type ActivationEventType =
  | 'signed_up'
  | 'email_verified'
  | 'profile_complete'
  | 'payout_connected'
  | 'first_customer_added'
  | 'first_quote_sent'
  | 'first_job_scheduled'
  | 'first_paid_invoice'

export interface EmitActivationEventInput {
  userId: string
  subAccountId?: string | null
  eventType: ActivationEventType
  metadata?: Record<string, unknown>
}

// `first_*` events are idempotent across re-fires: callers MAY emit on
// every successful save (e.g. CustomerFormModal) and the helper drops
// the second-onward attempt. Other events rely on caller-side
// transition detection.
function isFirstEvent(eventType: ActivationEventType): boolean {
  return eventType.startsWith('first_')
}

/**
 * Fire-and-forget activation event emit from the browser.
 *
 * Never throws. Logs console.warn on insert failure. Returns Promise<void>
 * so callers can `void emitActivationEvent({...})` without awaiting.
 */
export async function emitActivationEvent(input: EmitActivationEventInput): Promise<void> {
  try {
    const { userId, subAccountId, eventType, metadata } = input
    if (!userId) {
      console.warn('[activationEvents] emit skipped — missing userId', { eventType })
      return
    }

    // For first_* events, dedupe at the application layer. The EXISTS
    // check uses the per-user index; cost is one round-trip.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Lane 2 ships the table; types regen happens post-merge.
    const untyped = supabase as any
    if (isFirstEvent(eventType)) {
      const { data: existing, error: selectErr } = await untyped
        .from('activation_events')
        .select('id')
        .eq('user_id', userId)
        .eq('event_type', eventType)
        .limit(1)
        .maybeSingle()
      if (selectErr && selectErr.code !== 'PGRST116') {
        console.warn('[activationEvents] EXISTS check failed', { eventType, err: selectErr })
        // Fall through — better to risk a duplicate than to lose the
        // first ever activation row.
      } else if (existing) {
        return
      }
    }

    const { error: insertErr } = await untyped.from('activation_events').insert({
      user_id: userId,
      sub_account_id: subAccountId ?? null,
      event_type: eventType,
      metadata: metadata ?? {},
    })
    if (insertErr) {
      console.warn('[activationEvents] insert failed', { eventType, err: insertErr })
    }
  } catch (err) {
    console.warn('[activationEvents] unexpected throw', {
      eventType: input.eventType,
      err,
    })
  }
}

// ---------------------------------------------------------------------
// Server-side parallel (deno edge functions).
//
// Edge functions can't reach the browser supabase singleton, so callers
// pass their own service-role client. Same fire-and-forget semantics:
// failures log to console.warn and never bubble. Used by whop-webhook
// on payment.succeeded for the first_paid_invoice surface.

export interface ServerSupabaseClientLike {
  // We only call .from('activation_events').insert / .select. Loose
  // shape to avoid coupling to a specific supabase-js generic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from: (table: string) => any
}

export async function emitActivationEventServer(
  client: ServerSupabaseClientLike,
  input: EmitActivationEventInput,
): Promise<void> {
  try {
    const { userId, subAccountId, eventType, metadata } = input
    if (!userId) {
      console.warn('[activationEvents] server emit skipped — missing userId', { eventType })
      return
    }
    if (isFirstEvent(eventType)) {
      const { data: existing, error: selectErr } = await client
        .from('activation_events')
        .select('id')
        .eq('user_id', userId)
        .eq('event_type', eventType)
        .limit(1)
        .maybeSingle()
      if (selectErr && selectErr.code !== 'PGRST116') {
        console.warn('[activationEvents] server EXISTS check failed', {
          eventType,
          err: selectErr,
        })
      } else if (existing) {
        return
      }
    }
    const { error: insertErr } = await client.from('activation_events').insert({
      user_id: userId,
      sub_account_id: subAccountId ?? null,
      event_type: eventType,
      metadata: metadata ?? {},
    })
    if (insertErr) {
      console.warn('[activationEvents] server insert failed', {
        eventType,
        err: insertErr,
      })
    }
  } catch (err) {
    console.warn('[activationEvents] server unexpected throw', {
      eventType: input.eventType,
      err,
    })
  }
}
